import { CardTile } from "./CardTile";

const SELECT_CLASS =
  "mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500";

function AnalyticsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DeckUtilityBar({ deckName, totalCards, startingThreat, onAnalytics }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-800 bg-slate-950/95 backdrop-blur"
      role="toolbar"
      aria-label="Deck utilities"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="min-w-0">
          {deckName ? (
            <p className="truncate text-sm font-medium text-slate-100">{deckName}</p>
          ) : null}
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-slate-400">
            <p>
              <span className="font-semibold tabular-nums text-amber-100/90">{totalCards}</span>
              <span className="text-slate-500"> {totalCards === 1 ? "card" : "cards"}</span>
            </p>
            <p>
              <span className="text-slate-500">Starting threat </span>
              <span className="font-semibold tabular-nums text-amber-100/90">{startingThreat}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onAnalytics?.()}
          className="flex shrink-0 items-center gap-2 rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 transition hover:border-amber-500/60 hover:bg-slate-800 hover:text-amber-100"
          aria-label="Deck analytics"
        >
          <AnalyticsIcon className="h-5 w-5" />
          <span>Analytics</span>
        </button>
      </div>
    </div>
  );
}

export function Deck({
  signedIn,
  deck,
  deckSectionsByType,
  deckNameDraft,
  onDeckNameDraftChange,
  // saveAsName,
  // onSaveAsNameChange,
  savingDeckMeta,
  updateDeckMessage,
  onSaveDeckName,
  // savingCopy,
  // saveCopyMessage,
  // onSaveCopy,
  onGoToCards,
  onOpenCard,
  onDeckAdd,
  onDeckRemove,
  onAnalytics,
}) {
  if (!signedIn) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8">
        <p className="text-center text-slate-400">Sign in to view and edit your deck.</p>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8">
        <p className="text-center text-sm text-slate-500">Loading deck…</p>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 py-8 pb-28">
        <div className="mb-6 rounded-lg border border-slate-700 bg-slate-900/50 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="text-sm font-medium text-slate-200">Current deck</h3>
            <p className="text-sm text-slate-400">
              <span className="text-slate-500">Starting threat </span>
              <span className="font-semibold tabular-nums text-amber-100/90">{deck.starting_threat ?? 0}</span>
            </p>
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <label htmlFor="deck-name" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Deck name
            </label>
            <input
              id="deck-name"
              type="text"
              value={deckNameDraft}
              onChange={(e) => onDeckNameDraftChange(e.target.value)}
              className={SELECT_CLASS.replace("mt-1", "mt-1 block")}
              autoComplete="off"
            />
          </div>
          <button
            type="button"
            onClick={() => void onSaveDeckName()}
            disabled={
              savingDeckMeta || !deckNameDraft.trim() || deckNameDraft.trim() === (deck.name ?? "").trim()
            }
            className="rounded-md border border-slate-500 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-100 hover:border-amber-500/50 hover:bg-slate-700/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingDeckMeta ? "Saving…" : "Save deck name"}
          </button>
        </div>
        {updateDeckMessage && (
          <p
            className={
              updateDeckMessage.kind === "ok" ? "mt-2 text-sm text-emerald-400" : "mt-2 text-sm text-red-400"
            }
          >
            {updateDeckMessage.text}
          </p>
        )}
      </div>

      {/* Save a copy to My decks — hidden for now
      <div className="mb-8 rounded-lg border border-slate-700 bg-slate-900/50 p-4">
        <h3 className="text-sm font-medium text-slate-200">Save a copy to My decks</h3>
        <p className="mt-1 text-xs text-slate-500">
          Your working deck stays the same. If the name is still the default{" "}
          <span className="text-slate-400">My deck</span>, a number is added so each saved copy has its own title.
        </p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <label htmlFor="save-as-name" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Name for saved copy
            </label>
            <input
              id="save-as-name"
              type="text"
              value={saveAsName}
              onChange={(e) => onSaveAsNameChange(e.target.value)}
              className={SELECT_CLASS.replace("mt-1", "mt-1 block")}
              autoComplete="off"
            />
          </div>
          <button
            type="button"
            onClick={() => void onSaveCopy()}
            disabled={savingCopy}
            className="rounded-md border border-amber-600/70 bg-amber-950/90 px-4 py-2 text-sm font-medium text-amber-100 shadow hover:bg-amber-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingCopy ? "Saving…" : "Save copy"}
          </button>
        </div>
        {saveCopyMessage && (
          <p
            className={
              saveCopyMessage.kind === "ok" ? "mt-2 text-sm text-emerald-400" : "mt-2 text-sm text-red-400"
            }
          >
            {saveCopyMessage.text}
          </p>
        )}
      </div>
      */}

      {deck.lines.length === 0 ? (
        <p className="mt-12 text-center text-slate-500">
          Your deck is empty. Switch to the{" "}
          <button type="button" className="font-medium text-amber-400 hover:underline" onClick={onGoToCards}>
            Cards
          </button>{" "}
          tab to add cards.
        </p>
      ) : (
        <div className="space-y-12">
          {deckSectionsByType.map((section) => (
            <section key={section.key} aria-labelledby={`deck-type-${section.key}`}>
              <h3
                id={`deck-type-${section.key}`}
                className="mb-4 border-b border-slate-700 pb-2 text-sm font-semibold uppercase tracking-wide text-amber-200/90"
              >
                {section.title} ({section.totalQuantity})
              </h3>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
                {section.lines.map((line) => (
                  <CardTile
                    key={line.card_id}
                    card={line.card}
                    onOpen={onOpenCard}
                    signedIn={signedIn}
                    deckQuantity={line.quantity}
                    onDeckAdd={() => onDeckAdd(line.card.id)}
                    onDeckRemove={() => onDeckRemove(line.card.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      </div>

      <DeckUtilityBar
        deckName={deck.name}
        totalCards={deck.total_cards}
        startingThreat={deck.starting_threat ?? 0}
        onAnalytics={onAnalytics}
      />
    </>
  );
}
